import { createFileRoute, Link } from "@tanstack/react-router";
import { PageHero } from "@/components/page-hero";
import { metaDesc } from "@/lib/seo-description";
import photoboothHero from "@/assets/photobooth-avignon-mariage.jpg.asset.json";
import photoboothSalon from "@/assets/photobooth-salon-mariage.jpg.asset.json";
import photoboothCollage from "@/assets/photobooth-collage-invites.jpg.asset.json";


import { ReviewsProof } from "@/components/reviews-proof";
import { ORGANIZATION_SCHEMA } from "@/lib/schema-org";

const START_PRICE = 190;
const HERO = photoboothHero.url;
const HERO_ABS = `https://dj-val.fr${photoboothHero.url}`;
const COLLAGE_ALT = "Borne photobooth blanche avec écran tactile et ring light : photos de mariés et d'invités en train de poser avec accessoires";
const CANONICAL = "https://dj-val.fr/location-photobooth-avignon";

const ZONES = [
  { name: "Cavaillon", detail: "Atelier sur place : retrait et récupération de la borne inclus dans le tarif de 190 €." },
  { name: "Avignon", detail: "Livraison en option — intra-muros et périphérie (Montfavet, Les Angles)." },
  { name: "Le Pontet", detail: "À 25 minutes de l'atelier : salles de réception et domaines, livraison en option." },
  { name: "Sorgues", detail: "Mas et salles communales le long de l'Ouvèze, installation en 30 minutes." },
  { name: "Villeneuve-lès-Avignon", detail: "Rive gardoise : chartreuse, mas et jardins, livraison possible en option." },
  { name: "Morières-lès-Avignon", detail: "À 20 minutes de Cavaillon : bastides et salles des fêtes, mise en place rapide." },
  { name: "Carpentras", detail: "Livraison et installation possibles en option, sur devis." },
  { name: "Monteux", detail: "Domaines et salles proches du parc Spirou, alimentation 230 V suffisante." },
  { name: "Orange", detail: "Mas, caveaux et salles des fêtes : installation en 30 minutes, en option." },
  { name: "L'Isle-sur-la-Sorgue", detail: "Domaines et guinguettes bord de Sorgue, alimentation 230 V suffisante." },
  { name: "Châteaurenard", detail: "Bouches-du-Rhône limitrophe, à 15 minutes de l'atelier de Cavaillon." },
  { name: "Pertuis", detail: "Sud Luberon : bastides et domaines viticoles, livraison selon la distance." },
  { name: "Luberon & Alpilles", detail: "Gordes, Ménerbes, Saint-Rémy, Les Baux — livraison en option selon la distance." },
];

const INCLUDED = [
  "Borne photobooth à écran tactile",
  "Ring light intégré pour des photos lumineuses",
  "Bac d'accessoires : chapeaux, lunettes, perruques, pancartes",
  "Photos numériques illimitées sur toutes les prises de vue",
  "Imprimante dye-sub + tirages 10×15 illimités (forfait 390 €)",
  "Livraison, installation et démontage (forfait 390 €)",
];

const FAQ = [
  {
    q: "Combien coûte la location d'un photobooth à Avignon ?",
    a: "Deux formules. La formule digitale à partir de 190 € la soirée : borne + écran tactile + photos numériques illimitées, retrait et retour à Cavaillon. Le forfait illimité à 390 € la soirée : ajoute les tirages 10×15 illimités, la livraison, l'installation, les réglages et le démontage par DJ Val sur Avignon et dans le Vaucluse.",
  },
  {
    q: "Quelle est la différence entre la formule à 190 € et le forfait à 390 € ?",
    a: "À 190 €, vous louez la borne en autonomie : vos invités prennent leurs photos et elles sont toutes enregistrées en numérique. À 390 €, tout est inclus : impressions physiques 10×15 illimitées + livraison + installation + démontage sur place. Les deux formules incluent l'écran tactile, le ring light et le bac d'accessoires.",
  },
  {
    q: "Le photobooth est-il livré et installé ?",
    a: "Avec le forfait à 390 €, oui : livraison, installation, réglages et démontage sont inclus. Avec la formule à 190 €, la borne est à retirer à Cavaillon et à ramener le lendemain ; la livraison peut être ajoutée en option si vous préférez ne rien gérer.",
  },
  {
    q: "Que comprend exactement la location ?",
    a: "La borne photobooth à écran tactile, le ring light, le bac d'accessoires (chapeaux, lunettes, perruques, pancartes) et les photos numériques illimitées. Le forfait à 390 € ajoute l'imprimante dye-sub qualité labo avec tirages 10×15 illimités, la livraison, l'installation, les réglages et le démontage par DJ Val.",
  },
  {
    q: "Les impressions sont-elles vraiment illimitées ?",
    a: "Oui, dans le forfait à 390 € : sans compteur ni supplément, vos invités impriment autant de tirages 10×15 qu'ils le souhaitent pendant toute la soirée. Les consommables (papier et cartouches) sont fournis en quantité suffisante.",
  },
  {
    q: "Peut-on louer le photobooth avec la prestation DJ ?",
    a: "C'est la formule la plus demandée pour les mariages en Vaucluse : photobooth + DJ le même soir, avec une seule installation, un seul interlocuteur et un tarif combiné plus avantageux que deux prestataires séparés. Dans ce cas, la borne est livrée et installée par mes soins.",
  },
  {
    q: "Récupère-t-on les photos en numérique ?",
    a: "Oui, dans les deux formules : toutes les prises de vue sont enregistrées. Avec le forfait à 390 €, elles vous sont transmises via une galerie privée en ligne dans les 48 h suivant l'événement.",
  },
  {
    q: "Où récupère-t-on la borne photobooth ?",
    a: "À l'atelier de DJ Val à Cavaillon (84300), au 106 Grand Rue. Le retrait se fait généralement la veille ou le matin de l'événement, la restitution le lendemain. Un simple coffre de voiture suffit, la borne se transporte démontée.",
  },
  {
    q: "Livrez-vous le photobooth autour d'Avignon (Le Pontet, Sorgues, Villeneuve-lès-Avignon) ?",
    a: "Oui. Toute la couronne avignonnaise est desservie depuis Cavaillon : Le Pontet, Sorgues, Villeneuve-lès-Avignon, Morières-lès-Avignon, Montfavet, Monteux et Châteaurenard, soit 20 à 35 minutes de route. La livraison, l'installation et le démontage sont compris dans le forfait à 390 € ; avec la formule à 190 €, ils s'ajoutent en option sur devis selon la distance. J'arrive en général deux heures avant le début de la soirée et la mise en place prend 30 minutes.",
  },
];

const PHOTOBOOTH_PRODUCT_LD = {
  "@context": "https://schema.org",
  "@type": "Product",
  name: "Location photobooth Avignon",
  description:
    "Borne photobooth à écran tactile avec ring light et accessoires. Photos numériques illimitées dès 190 € la soirée. Forfait illimité à 390 € avec tirages 10×15, livraison, installation et démontage.",
  image: HERO_ABS,
  category: "Location photobooth événementiel",
  brand: { "@type": "Brand", name: "DJ Val" },
  offers: {
    "@type": "Offer",
    priceCurrency: "EUR",
    price: String(START_PRICE),
    availability: "https://schema.org/InStock",
    seller: ORGANIZATION_SCHEMA,
    areaServed: ["Avignon", "Le Pontet", "Sorgues", "Villeneuve-lès-Avignon", "Morières-lès-Avignon", "Carpentras", "Monteux", "Orange", "Cavaillon", "L'Isle-sur-la-Sorgue", "Châteaurenard", "Pertuis", "Vaucluse", "Luberon", "Alpilles"],
    url: CANONICAL,
    hasMerchantReturnPolicy: {
      "@type": "MerchantReturnPolicy",
              url: "https://dj-val.fr/conditions-de-location",
      applicableCountry: "FR",
      returnPolicyCategory: "https://schema.org/MerchantReturnNotPermitted",
    },
    shippingDetails: {
      "@type": "OfferShippingDetails",
      shippingRate: { "@type": "MonetaryAmount", value: "0", currency: "EUR" },
      shippingDestination: { "@type": "DefinedRegion", addressCountry: "FR" },
      deliveryTime: {
        "@type": "ShippingDeliveryTime",
        handlingTime: { "@type": "QuantitativeValue", minValue: 0, maxValue: 1, unitCode: "DAY" },
        transitTime: { "@type": "QuantitativeValue", minValue: 0, maxValue: 1, unitCode: "DAY" },
      },
    },
  },
};

const PHOTOBOOTH_BREADCRUMB_LD = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Accueil", item: "https://dj-val.fr/" },
    { "@type": "ListItem", position: 2, name: "Location photobooth Avignon", item: CANONICAL },
  ],
};

export const Route = createFileRoute("/location-photobooth-avignon")({
  head: () => ({
    meta: [
      { title: `Photobooth Avignon dès ${START_PRICE} € la soirée | DJ Val` },
      {
        name: "description",
        content:
          metaDesc("Photobooth à Avignon dès 190 € la soirée : borne tactile, photos illimitées, accessoires. Forfait tout inclus 390 € avec tirages. 41 avis Google. Réponse en 24 h.")
      },
      { property: "og:title", content: `Photobooth Avignon dès ${START_PRICE} € la soirée | DJ Val` },
      {
        property: "og:description",
        content:
          metaDesc("Photobooth à Avignon dès 190 € la soirée : borne tactile, photos illimitées, accessoires. Forfait tout inclus 390 € avec tirages. 41 avis Google. Réponse en 24 h.")
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: CANONICAL },
      { property: "og:image", content: HERO_ABS },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: `Location photobooth Avignon dès ${START_PRICE} € | DJ Val` },
      {
        name: "twitter:description",
        content: "Location photobooth à Avignon et dans le Vaucluse dès 190 € : borne tactile, photos illimitées. Forfait tout inclus à 390 €. Devis gratuit en 24 h.",
      },
      { name: "twitter:image", content: HERO_ABS },

    ],
    links: [{ rel: "canonical", href: CANONICAL }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(PHOTOBOOTH_PRODUCT_LD) },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: FAQ.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
      { type: "application/ld+json", children: JSON.stringify(PHOTOBOOTH_BREADCRUMB_LD) },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <div className="bg-background text-muted-foreground">
      <PageHero
        image={HERO}
        eyebrow="Location photobooth — Vaucluse"
        title={
          <>
            Location photobooth <span className="italic">Avignon</span>
          </>
        }
        subtitle="À partir de 190 € la soirée, photos numériques illimitées comprises. Forfait illimité à 390 € avec tirages 10×15, livraison et installation."
        primary={{ label: "Demander un devis", to: "/contactez-nous" }}
        secondary={{ label: "07 67 90 86 67", href: "tel:+33767908667" }}
      />
      <div className="px-6 -mt-6 relative z-10 max-w-4xl mx-auto">
        <div className="inline-flex items-baseline gap-3 border border-[var(--gold-line)] bg-background px-6 py-4 shadow-sm">
          <span className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">À partir de</span>
          <span className="font-serif-display text-3xl text-[var(--gold)]">{START_PRICE} <span className="text-lg align-top">€</span></span>
          <span className="text-xs text-muted-foreground">la soirée</span>
        </div>
      </div>
      <ReviewsProof />


      <section className="py-16 px-6 max-w-4xl mx-auto">
        <h2 className="font-serif-display text-3xl md:text-4xl text-foreground mb-6">
          Location de photobooth à Avignon et dans tout le Vaucluse
        </h2>
        <div className="space-y-4 text-sm md:text-base leading-relaxed font-light">
          <p>
            Basé à Cavaillon, à vingt minutes d'Avignon, je loue une borne photobooth professionnelle pour les
            mariages, anniversaires, soirées d'entreprise et fêtes de famille du Vaucluse. Écran tactile, imprimante
            qualité labo : vos invités appuient, posent, et récupèrent leur tirage 10×15 quelques secondes plus tard.
          </p>

          <p>
            La formule de base est en retrait à mon atelier de Cavaillon : vous récupérez la borne, vous la branchez
            sur une simple prise 230 V et vous la ramenez après la fête. Si vous préférez ne rien gérer le jour J, la
            livraison, l'installation, les réglages et le démontage sont proposés en option — j'interviens alors moi-même
            sur place, y compris en cas de bourrage papier ou de réglage à reprendre pendant la soirée.
          </p>

        </div>
      </section>

      <section className="py-10 px-6">
        <div className="max-w-4xl mx-auto rounded-sm border border-[var(--gold)]/30 bg-[var(--gold)]/10 p-8 text-center">
          <h2 className="font-serif-display text-2xl md:text-3xl text-foreground mb-3">
            Votre date est encore disponible ?
          </h2>
          <p className="text-sm md:text-base font-light mb-6 max-w-2xl mx-auto">
            Les samedis de mai à septembre partent plusieurs mois à l'avance. Vérifiez la disponibilité de votre photobooth en quelques clics.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link
              to="/contactez-nous"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-[var(--gold)] text-[var(--ink)] text-sm font-semibold uppercase tracking-wider hover:opacity-90 transition"
            >
              Vérifier ma date
              <span aria-hidden>→</span>
            </Link>
            <a
              href="tel:+33767908667"
              className="inline-flex items-center gap-2 px-8 py-3.5 border border-foreground/30 text-foreground text-sm font-semibold uppercase tracking-wider hover:border-[var(--gold)] transition"
              style={{ fontFamily: "Arial, Helvetica, sans-serif" }}
            >
              07 67 90 86 67
            </a>
          </div>
        </div>
      </section>

      <section className="py-8 px-6 max-w-4xl mx-auto">
        <figure className="relative overflow-hidden rounded-sm border border-foreground/10 bg-foreground/[0.02]">
          <img
            src={photoboothSalon.url}
            alt="Borne photobooth blanche avec ring light, installée dans une salle de réception élégante avec fleurs et guirlandes"
            width={900}
            height={1200}
            loading="lazy"
            decoding="async"
            className="w-full h-auto object-cover"
          />
          <figcaption className="px-5 py-4 text-xs text-muted-foreground font-light text-center">
            Borne photobooth premium · écran tactile · ring light · installation chic pour mariage et événement
          </figcaption>
        </figure>
      </section>

      <section className="py-16 px-6 bg-foreground/[0.03]">

        <div className="max-w-4xl mx-auto">
          <h2 className="font-serif-display text-2xl md:text-3xl text-foreground mb-8">Ce que comprend la location</h2>
          <ul className="grid sm:grid-cols-2 gap-3 text-sm font-light">
            {INCLUDED.map((i) => (
              <li key={i} className="flex gap-3">
                <span className="text-[var(--gold)] shrink-0">—</span>
                <span>{i}</span>
              </li>
            ))}
          </ul>
          <div className="mt-10 grid md:grid-cols-2 gap-5">
            <div className="border border-foreground/10 bg-background p-6 flex flex-col">
              <p className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-3">Formule digitale</p>
              <p className="font-serif-display text-4xl text-foreground mb-2">
                190 <span className="text-lg align-top">€</span>
              </p>
              <p className="text-xs text-muted-foreground mb-6">la soirée · photos numériques illimitées comprises</p>
              <ul className="text-sm font-light space-y-2 mb-8 flex-1">
                <li className="flex gap-2"><span className="text-[var(--gold)]">—</span> Retrait à Cavaillon</li>
                <li className="flex gap-2"><span className="text-[var(--gold)]">—</span> Borne tactile + accessoires</li>
                <li className="flex gap-2"><span className="text-[var(--gold)]">—</span> Toutes les photos en numérique</li>
              </ul>
              <p className="text-xs text-muted-foreground">Retour le lendemain à l'atelier.</p>
            </div>

            <div className="border border-[var(--gold)]/40 bg-background p-6 flex flex-col relative">
              <span className="absolute top-0 right-0 bg-[var(--gold)] text-[var(--ink)] text-[9px] font-semibold uppercase tracking-wider px-3 py-1">
                Le plus demandé
              </span>
              <p className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-3">Forfait illimité</p>
              <p className="font-serif-display text-4xl text-foreground mb-2">
                390 <span className="text-lg align-top">€</span>
              </p>
              <p className="text-xs text-muted-foreground mb-6">la soirée · tout inclus</p>
              <ul className="text-sm font-light space-y-2 mb-8 flex-1">
                <li className="flex gap-2"><span className="text-[var(--gold)]">—</span> Tirages 10×15 illimités</li>
                <li className="flex gap-2"><span className="text-[var(--gold)]">—</span> Livraison + installation</li>
                <li className="flex gap-2"><span className="text-[var(--gold)]">—</span> DJ Val sur place le jour J</li>
                <li className="flex gap-2"><span className="text-[var(--gold)]">—</span> Galerie privée en ligne</li>
              </ul>
              <p className="text-xs text-muted-foreground">Sur Avignon, Cavaillon, Carpentras, Orange et L'Isle-sur-la-Sorgue.</p>
            </div>
          </div>
          <div className="mt-10 text-center">
            <Link
              to="/contactez-nous"
              className="inline-flex items-center gap-2 px-10 py-4 bg-[var(--gold)] text-[var(--ink)] text-sm font-semibold uppercase tracking-wider hover:opacity-90 transition shadow-lg"
            >
              Réserver mon photobooth
              <span aria-hidden>→</span>
            </Link>
            <p className="mt-4 text-xs text-muted-foreground font-light">
              Devis gratuit sous 24 h · sans engagement
            </p>
          </div>
        </div>
      </section>

      <section className="py-16 px-6 max-w-4xl mx-auto">
        <figure className="relative overflow-hidden rounded-sm border border-foreground/10 bg-foreground/[0.02]">
          <img
            src={photoboothCollage.url}
            alt={COLLAGE_ALT}
            width={1200}
            height={1600}
            loading="lazy"
            decoding="async"
            className="w-full h-auto object-cover"
          />
          <figcaption className="px-5 py-4 text-xs text-muted-foreground font-light text-center">
            Borne photobooth à écran tactile · impressions instantanées 10×15 · accessoires inclus
          </figcaption>
        </figure>
      </section>

      <section className="py-16 px-6 max-w-4xl mx-auto">
        <h2 className="font-serif-display text-2xl md:text-3xl text-foreground mb-4">Zones d'intervention en Vaucluse</h2>
        <p className="text-sm md:text-base font-light leading-relaxed mb-8 max-w-3xl">
          L'atelier est à Cavaillon, au centre du Vaucluse : toute la couronne avignonnaise est à moins de 35 minutes
          de route — Avignon, Le Pontet, Sorgues, Villeneuve-lès-Avignon, Morières-lès-Avignon et Montfavet — comme le
          Comtat (Carpentras, Monteux, Orange), la Sorgue et le Luberon. Retrait à l'atelier dans la formule à 190 €,
          livraison et installation comprises dans le forfait à 390 €.
        </p>
        <div className="grid sm:grid-cols-2 gap-6">
          {ZONES.map((z) => (
            <div key={z.name} className="border-l border-[var(--gold)]/40 pl-4">
              <h3 className="text-foreground text-base mb-1">Photobooth {z.name}</h3>
              <p className="text-sm font-light">{z.detail}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="px-6 pb-8">
        <div className="max-w-4xl mx-auto border border-[var(--gold)]/40 bg-[var(--gold)]/[0.06] p-8 sm:p-10 text-center">
          <h2 className="font-serif-display text-2xl md:text-3xl text-foreground">
            Vous cherchez aussi un <span className="italic">DJ</span> ?
          </h2>
          <p className="mt-4 text-sm font-light leading-relaxed max-w-2xl mx-auto">
            Je suis DJ mariage en Vaucluse depuis 20 ans. En pack DJ + photobooth, la livraison,
            l'installation et le démontage de la borne sont offerts, et j'anime le photobooth
            pendant la soirée : un seul prestataire pour la musique, la lumière et les souvenirs.
          </p>
          <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/dj-mariage"
              className="bg-[var(--gold)] text-foreground px-8 py-3 text-[10px] font-semibold uppercase tracking-[0.25em] hover:brightness-110 transition-all"
            >
              Voir la prestation DJ mariage
            </Link>
            <Link
              to="/contactez-nous"
              className="border border-foreground/20 text-foreground px-8 py-3 text-[10px] font-semibold uppercase tracking-[0.25em] hover:border-[var(--gold)] transition-colors"
            >
              Devis DJ + photobooth
            </Link>
          </div>
        </div>
      </section>

      <section className="py-16 px-6 bg-foreground/[0.03]">
        <div className="max-w-3xl mx-auto">
          <h2 className="font-serif-display text-2xl md:text-3xl text-foreground mb-8">Questions fréquentes</h2>
          <div className="space-y-6">
            {FAQ.map((f) => (
              <div key={f.q}>
                <h3 className="text-foreground text-base mb-2">{f.q}</h3>
                <p className="text-sm font-light leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-10 px-6">
        <div className="max-w-3xl mx-auto rounded-sm border border-foreground/10 bg-background p-8 text-center shadow-sm">
          <h2 className="font-serif-display text-2xl md:text-3xl text-foreground mb-3">
            Encore une question ?
          </h2>
          <p className="text-sm md:text-base font-light mb-6">
            Je vous réponds directement par téléphone ou WhatsApp. Devis gratuit et sans engagement.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href="https://wa.me/33767908667?text=Bonjour%20Val%2C%20je%20souhaiterais%20un%20devis%20pour%20la%20location%20d'un%20photobooth."
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-8 py-3.5 bg-[#25D366] text-white text-sm font-semibold uppercase tracking-wider hover:opacity-90 transition"
            >
              <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
              </svg>
              WhatsApp
            </a>
            <a
              href="tel:+33767908667"
              className="inline-flex items-center gap-2 px-8 py-3.5 border border-foreground/30 text-foreground text-sm font-semibold uppercase tracking-wider hover:border-[var(--gold)] transition"
              style={{ fontFamily: "Arial, Helvetica, sans-serif" }}
            >
              07 67 90 86 67
            </a>
          </div>
        </div>
      </section>

      <section className="py-16 px-6">
        <div className="max-w-3xl mx-auto text-center rounded-sm border border-[var(--gold)]/40 bg-[var(--gold)]/[0.06] p-10 md:p-14">
          <h2 className="font-serif-display text-3xl md:text-4xl text-foreground mb-4">
            Réservez votre photobooth pour votre date
          </h2>
          <p className="text-sm md:text-base font-light mb-8 max-w-xl mx-auto">
            Les samedis de mai à septembre partent plusieurs mois à l'avance en Vaucluse. Réponse sous 24 h.
          </p>
          <div className="flex flex-col sm:flex-row flex-wrap justify-center gap-4">
            <Link
              to="/contactez-nous"
              className="inline-flex items-center justify-center gap-2 px-10 py-4 bg-[var(--gold)] text-[var(--ink)] text-sm font-semibold uppercase tracking-wider hover:opacity-90 transition shadow-lg"
            >
              Demander un devis
              <span aria-hidden>→</span>
            </Link>
            <a
              href="tel:+33767908667"
              className="inline-flex items-center justify-center gap-2 px-10 py-4 border border-foreground/30 text-foreground text-sm font-semibold uppercase tracking-wider hover:border-[var(--gold)] transition"
              style={{ fontFamily: "Arial, Helvetica, sans-serif" }}
            >
              07 67 90 86 67
            </a>
          </div>
          <p className="mt-6 text-xs text-muted-foreground font-light">
            Devis gratuit · sans engagement · disponibilité sous 24 h
          </p>
        </div>
        <p className="mt-10 text-xs text-center max-w-3xl mx-auto">
          À voir aussi :{" "}
          <Link to="/location-materiel/$slug" params={{ slug: "photobooth" }} className="text-[var(--gold)] hover:underline">
            fiche technique du photobooth
          </Link>
          {" · "}
          <Link to="/location-karaoke-vaucluse" className="text-[var(--gold)] hover:underline">
            location pack karaoké Avignon & Vaucluse
          </Link>
          {" · "}
          <Link to="/dj-mariage/$ville" params={{ ville: "avignon" }} className="text-[var(--gold)] hover:underline">
            DJ mariage Avignon
          </Link>
        </p>
      </section>
    </div>
  );
}
